import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  MoreVertical,
  UserMinus,
  Send,
  CheckCircle2,
  XCircle,
  AlertCircle,
  X,
  Loader2,
  Clock,
} from 'lucide-react';
import {
  api,
  parseApiError,
  type OrgClanMemberEntry,
} from '../lib/api';
import { Card } from './Card';
import { ConfirmModal } from './ConfirmModal';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { displayClanName } from '../lib/displayName';

// ── Helpers ───────────────────────────────────────────────────────────────────

function shortId(id: string): string {
  return `…${id.slice(-6)}`;
}

// Display-only everywhere it's called (verified all 4 call sites — rows,
// pending-request/invite tiles, remove-confirm modal title; keys/mutations
// all use clan.clanId/req.clanId/inv.clanId directly, never this). Safe to
// route both branches through displayClanName.
function bareName(clanId: string, name?: string): string {
  if (name) return displayClanName(name);
  return displayClanName(clanId.replace(/^CLAN#/, ''));
}

// ── MemberClanRow — per-row sub-component (unexported) ────────────────────────
// Self-contained: owns its own menu state + ref so click-outside detection
// works correctly regardless of list size.

type MemberRowProps = {
  clan: OrgClanMemberEntry;
  index: number;
  canManage: boolean;
  onRemoveRequest: (c: OrgClanMemberEntry) => void;
};

function MemberClanRow({ clan, index, canManage, onRemoveRequest }: MemberRowProps) {
  // ALL hooks at top before any early return
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const displayName = bareName(clan.clanId, clan.name);

  return (
    <div
      className={cn(
        `${SETTLE} group relative flex items-center justify-between border border-iron-800/60 bg-iron-950 px-4 py-3 transition-colors`,
        'hover:border-forge-900/60 hover:bg-iron-950'
      )}
      style={settleDelay(index)}
    >
      {/* Identity */}
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-iron-700/50 bg-iron-950">
          <Users className="h-4 w-4 text-iron-400" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-black uppercase tracking-wide text-white">
            {displayName}
          </p>
          {clan.leaderId && (
            <p className="mt-0.5 text-[10px] text-iron-500">
              leader {clan.leaderName ?? shortId(clan.leaderId)}
            </p>
          )}
        </div>
      </div>

      {/* Action menu */}
      {canManage && (
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="flex h-7 w-7 items-center justify-center border border-iron-800 text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200"
            aria-label="Clan actions"
          >
            <MoreVertical className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full z-40 mt-1 w-44 border border-forge-900/60 bg-iron-950 shadow-forged">
              <div className="border-b border-forge-900/40 px-3 py-2">
                <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
                  Actions
                </p>
              </div>
              <div className="py-1">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onRemoveRequest(clan);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-300 transition-colors hover:bg-red-950/40 hover:text-red-200"
                >
                  <UserMinus className="h-3 w-3" strokeWidth={2.5} />
                  Remove from org
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── OrgClansPanel — exported ──────────────────────────────────────────────────

export function OrgClansPanel({
  orgId,
  canManage,
  isFounder = false,
}: {
  orgId: string;
  canManage: boolean;
  isFounder?: boolean;
}) {
  const qc = useQueryClient();

  // ALL hooks above any early return
  const [removeTarget, setRemoveTarget] = useState<OrgClanMemberEntry | null>(null);
  const [inviteInput, setInviteInput] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [mutError, setMutError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['orgClans', orgId],
    queryFn: () => api.orgClans(orgId),
    enabled: canManage,
  });

  const removeMut = useMutation({
    mutationFn: (clanId: string) => api.removeClanFromOrg(orgId, clanId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgClans', orgId] });
      setRemoveTarget(null);
    },
    onError: (e: Error) => {
      setMutError(parseApiError(e).message);
      setRemoveTarget(null);
    },
  });

  const approveMut = useMutation({
    mutationFn: (clanId: string) => api.approveClanRequest(orgId, clanId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgClans', orgId] }),
    onError: (e: Error) => setMutError(parseApiError(e).message),
  });

  const denyMut = useMutation({
    mutationFn: (clanId: string) => api.denyClanRequest(orgId, clanId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgClans', orgId] }),
    onError: (e: Error) => setMutError(parseApiError(e).message),
  });

  const inviteMut = useMutation({
    mutationFn: () => api.inviteClanToOrg(orgId, inviteInput.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgClans', orgId] });
      setInviteInput('');
      setInviteError('');
    },
    onError: (e: Error) => setInviteError(parseApiError(e).message),
  });

  // Guard: non-managers see nothing (double safety — mount in MyOrg is already gated)
  if (!canManage) return null;

  const memberClans = data?.memberClans ?? [];
  const pendingRequests = data?.pendingRequests ?? [];
  const pendingInvites = data?.pendingInvites ?? [];
  const requestOrApproveBusy = approveMut.isPending || denyMut.isPending;

  // Suppress unused-var warning for isFounder — may drive additional gates in a
  // future phase (e.g. force-remove without the 409 guard).
  void isFounder;

  return (
    <Card>
      {/* ── Header ── */}
      <div className="mb-4 flex items-center gap-2">
        <h2 className="flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          <Users className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
          Affiliated Clans
          {!isLoading && memberClans.length > 0 && (
            <span className="ml-1 font-mono text-[10px] font-normal text-iron-500">
              {memberClans.length}
            </span>
          )}
        </h2>
      </div>

      {/* ── Inline mutation error (dismissible) ── */}
      {mutError && (
        <div className="mb-3 flex items-center gap-2 rounded bg-red-950/50 px-3 py-2 text-xs text-red-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          <span className="flex-1">{mutError}</span>
          <button
            onClick={() => setMutError('')}
            className="ml-2 text-red-500 hover:text-red-300"
            aria-label="Dismiss error"
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* ── Loading state ── */}
      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-iron-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Loading clans…</span>
        </div>
      )}

      {!isLoading && (
        <>
          {/* ── 1. Member clans ── */}
          {memberClans.length > 0 && (
            <div className="mb-5">
              <p className="mb-2 font-display text-[9px] font-black uppercase tracking-[0.22em] text-iron-400">
                Members · {memberClans.length}
              </p>
              <div className="space-y-1">
                {memberClans.map((clan, i) => (
                  <MemberClanRow
                    key={clan.clanId}
                    clan={clan}
                    index={i}
                    canManage={canManage}
                    onRemoveRequest={setRemoveTarget}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── 2. Pending join requests ── */}
          {pendingRequests.length > 0 && (
            <div className="mb-5">
              <p className="mb-2 font-display text-[9px] font-black uppercase tracking-[0.22em] text-iron-400">
                Join requests · {pendingRequests.length}
              </p>
              <div className="space-y-1">
                {pendingRequests.map((req, i) => {
                  const displayName = bareName(req.clanId);
                  return (
                    <div
                      key={req.clanId}
                      className={cn(
                        `${SETTLE} flex items-center justify-between border border-iron-800/60 bg-iron-950 px-4 py-3`,
                        requestOrApproveBusy && 'opacity-60'
                      )}
                      style={settleDelay(i)}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-amber-700/30 bg-amber-950/20">
                          <Clock className="h-4 w-4 text-amber-400/70" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-display text-sm font-black uppercase tracking-wide text-white">
                            {displayName}
                          </p>
                          {req.requestedBy && (
                            <p className="mt-0.5 text-[10px] text-iron-500">
                              requested by {req.requestedByName ?? shortId(req.requestedBy)}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          onClick={() => approveMut.mutate(req.clanId)}
                          disabled={requestOrApproveBusy}
                          className={`${MORPH} flex items-center gap-1 border border-emerald-700/50 px-2.5 py-1 font-display text-[10px] font-black uppercase tracking-wider text-emerald-300 transition-colors hover:border-emerald-500 hover:text-emerald-100 disabled:opacity-40`}
                        >
                          {approveMut.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                          ) : (
                            <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
                          )}
                          Approve
                        </button>
                        <button
                          onClick={() => denyMut.mutate(req.clanId)}
                          disabled={requestOrApproveBusy}
                          className={`${MORPH} flex items-center gap-1 border border-red-800/50 px-2.5 py-1 font-display text-[10px] font-black uppercase tracking-wider text-red-300 transition-colors hover:border-red-600 hover:text-red-100 disabled:opacity-40`}
                        >
                          {denyMut.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                          ) : (
                            <XCircle className="h-3 w-3" strokeWidth={2.5} />
                          )}
                          Deny
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── 3. Pending invites (display only v1) ── */}
          {pendingInvites.length > 0 && (
            <div className="mb-5">
              <p className="mb-2 font-display text-[9px] font-black uppercase tracking-[0.22em] text-iron-400">
                Invites sent · awaiting response · {pendingInvites.length}
              </p>
              <div className="space-y-1">
                {pendingInvites.map((inv, i) => {
                  const displayName = bareName(inv.clanId);
                  return (
                    <div
                      key={inv.clanId}
                      className={`${SETTLE} flex items-center justify-between border border-iron-800/40 bg-iron-950 px-4 py-3`}
                      style={settleDelay(i)}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-iron-700/30 bg-iron-950">
                          <Send className="h-4 w-4 text-iron-500" strokeWidth={2.5} />
                        </div>
                        <p className="truncate font-display text-sm font-black uppercase tracking-wide text-iron-300">
                          {displayName}
                        </p>
                      </div>
                      <span className="font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-500">
                        pending
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Empty state ── */}
          {memberClans.length === 0 &&
            pendingRequests.length === 0 &&
            pendingInvites.length === 0 && (
              <p className="py-4 text-center text-xs text-iron-500">No affiliated clans yet.</p>
            )}

          {/* ── 4. Invite a clan form ── */}
          <div className={`${SETTLE} border-t border-iron-800/60 pt-4`}>
            <p className="mb-1 font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
              Invite a clan
            </p>
            <p className="mb-2 text-[11px] text-ink-meta">
              Type the clan's name (not a player's) — the invite goes to that clan's leader to accept.
            </p>
            <div className="flex gap-2">
              <input
                value={inviteInput}
                onChange={e => {
                  setInviteInput(e.target.value);
                  setInviteError('');
                }}
                placeholder="Clan name…"
                className="input flex-1 text-sm"
                disabled={inviteMut.isPending}
              />
              <button
                onClick={() => inviteMut.mutate()}
                disabled={!inviteInput.trim() || inviteMut.isPending}
                className={`${MORPH} btn-primary flex items-center gap-1.5 disabled:opacity-50`}
              >
                {inviteMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" strokeWidth={2.5} />
                )}
                Invite
              </button>
            </div>
            {inviteError && (
              <div className="mt-2 flex items-center gap-2 text-xs text-red-300">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                {inviteError}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Remove confirmation modal ── */}
      {removeTarget && (
        <ConfirmModal
          tone="danger"
          icon={UserMinus}
          title={`Remove ${bareName(removeTarget.clanId, removeTarget.name)} from org?`}
          body="The clan loses its org affiliation immediately."
          confirmLabel="Remove"
          confirmIcon={UserMinus}
          pending={removeMut.isPending}
          onConfirm={() => removeMut.mutate(removeTarget.clanId)}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
    </Card>
  );
}
